import { createContext, useContext, useEffect, useState, useCallback } from "react";
import api, { formatApiErrorDetail, wipeClientCaches } from "@/lib/api";

const AuthContext = createContext(null);

// SEC-001: mirror the current user's id into sessionStorage so the axios
// interceptor can stamp it on offline-queue items (drain refuses to replay
// under someone else's session on a shared browser).
function _stampUserId(u) {
  try {
    if (u && u.id) sessionStorage.setItem("ledger.user_id", String(u.id));
    else sessionStorage.removeItem("ledger.user_id");
  } catch { /* private-mode / storage disabled */ }
}

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null); // null = loading, false = anon, obj = user
  const [error, setError] = useState("");

  const refreshMe = useCallback(async () => {
    try {
      const { data } = await api.get("/auth/me");
      setUser(data); _stampUserId(data);
    } catch (err) {
      // 401 on cold-start is expected — log everything else for debugging.
      if (err?.response?.status && err.response.status !== 401) {
        console.warn("[Auth] /me failed:", err.response.status, err.message);
      }
      setUser(false); _stampUserId(null);
    }
  }, []);

  useEffect(() => {
    refreshMe();
  }, [refreshMe]);

  const login = async (email, password) => {
    setError("");
    try {
      const { data } = await api.post("/auth/login", { email, password });
      setUser(data); _stampUserId(data);
      return true;
    } catch (e) {
      setError(formatApiErrorDetail(e.response?.data?.detail) || e.message);
      return false;
    }
  };

  const startDemo = async () => {
    setError("");
    try {
      const { data } = await api.post("/auth/demo");
      setUser(data); _stampUserId(data);
      return true;
    } catch (e) {
      setError(formatApiErrorDetail(e.response?.data?.detail) || e.message);
      return false;
    }
  };

  const register = async (name, email, password) => {
    setError("");
    try {
      const { data } = await api.post("/auth/register", { name, email, password });
      setUser(data); _stampUserId(data);
      return true;
    } catch (e) {
      setError(formatApiErrorDetail(e.response?.data?.detail) || e.message);
      return false;
    }
  };

  const logout = async () => {
    try { await api.post("/auth/logout"); }
    catch (err) { console.debug("[Auth] logout server call failed:", err?.message); }
    // SEC-001: wipe every trace of this user from the browser BEFORE clearing
    // state — offline queue, ref cache, and Service-Worker API cache — so a
    // subsequent login on the same device starts fresh.
    try { await wipeClientCaches(); } catch { /* best-effort */ }
    // iter 72 — also clear the Personal/Business choice so the next sign-in
    // gets the workspace picker (never inherits a stale kind on a shared tab).
    try {
      sessionStorage.removeItem("ledger.workspace_kind");
      sessionStorage.removeItem("ledger.entity_id");
    } catch { /* private mode */ }
    _stampUserId(null);
    setUser(false);
  };

  const updateCurrency = async (currency) => {
    const { data } = await api.patch("/user/currency", { currency });
    setUser(data);
  };

  return (
    <AuthContext.Provider value={{ user, error, login, startDemo, register, logout, refreshMe, updateCurrency, setError }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  return useContext(AuthContext);
}
